import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'

/**
 * Selector de negocio para las pantallas que mezclan varias marcas.
 *
 * Una empresa puede tener negocios que no tienen nada que ver entre sí — una
 * tienda de productos y una empresa de servicios — y sus pedidos y sus tratos
 * caían en la misma lista. Este selector los separa.
 *
 * Es un menú y no una fila de botones: con ocho negocios la fila se partía en
 * varias líneas y empujaba el contenido hacia abajo. Un botón que dice cuál
 * está activo ocupa siempre lo mismo, y la lista se despliega solo al pedirla.
 *
 * La elección se recuerda en el navegador y por pantalla, porque quien trabaja
 * en Pedidos de una marca no quiere volver a elegirla cada vez, y puede estar
 * mirando otra distinta en el Pipeline.
 */

const ETIQUETA_TIPO = { productos: 'Productos', servicios: 'Servicios', ambos: 'Productos y servicios' }

/**
 * `rubro` limita la lista al negocio que corresponde a la pantalla: en Pedidos
 * no pinta nada una empresa de servicios, ni en Proyectos una tienda. Los
 * negocios de tipo "ambos" salen en las dos, porque venden las dos cosas.
 */
function encaja(negocio, rubro) {
  if (!rubro) return true
  const t = negocio.tipo || 'servicios'
  return t === 'ambos' || t === rubro
}

export default function SelectorNegocio({ valor, onChange, clave = 'general', incluirTodos = true, rubro = null }) {
  const [negocios, setNegocios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [abierto, setAbierto] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 })
  const disparador = useRef(null)
  const menu = useRef(null)
  const almacen = `negocio_activo_${clave}`

  useEffect(() => {
    let vivo = true
    api.get('/negocios')
      .then(({ data }) => {
        if (!vivo) return
        const lista = (Array.isArray(data) ? data : data.negocios || [])
          .filter(n => n.activo !== false && encaja(n, rubro))
        setNegocios(lista)

        // Se restaura la última elección, pero solo si ese negocio sigue estando
        // en la lista: uno guardado que ya no encaja con el rubro dejaría la
        // pantalla filtrando por algo que no se ve en el menú.
        let guardado = null
        try { guardado = localStorage.getItem(almacen) } catch { /* modo privado */ }
        const valido = guardado === 'todos' || lista.some(n => n.id === guardado)
        const inicial = valido ? guardado : (incluirTodos ? 'todos' : lista[0]?.id)
        if (inicial && inicial !== valor) onChange(inicial)
      })
      .catch(() => { if (vivo) setNegocios([]) })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
    // Solo al montar: recargar la lista en cada cambio de filtro no aporta nada
  }, [])

  // El menú va en un portal con position:fixed, así que se sitúa a mano desde
  // el botón: dentro de una cabecera con transform, un absolute se descoloca.
  const colocar = () => {
    const b = disparador.current
    if (!b) return
    const c = b.getBoundingClientRect()
    const ancho = Math.max(240, c.width)
    const alto = Math.min(380, 56 + negocios.length * 52)
    const cabeAbajo = window.innerHeight - c.bottom - 8 > alto
    setPos({
      top: cabeAbajo ? c.bottom + 6 : Math.max(8, c.top - 6 - alto),
      left: Math.min(Math.max(8, c.left), window.innerWidth - ancho - 8),
      width: ancho,
      maxHeight: Math.min(380, cabeAbajo ? window.innerHeight - c.bottom - 16 : c.top - 16),
    })
  }

  useEffect(() => {
    if (!abierto) return
    colocar()
    const cerrarFuera = (e) => {
      if (menu.current?.contains(e.target) || disparador.current?.contains(e.target)) return
      setAbierto(false)
    }
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', cerrarFuera)
    document.addEventListener('keydown', esc)
    window.addEventListener('scroll', colocar, true)
    window.addEventListener('resize', colocar)
    return () => {
      document.removeEventListener('mousedown', cerrarFuera)
      document.removeEventListener('keydown', esc)
      window.removeEventListener('scroll', colocar, true)
      window.removeEventListener('resize', colocar)
    }
    // eslint-disable-next-line
  }, [abierto, negocios.length])

  const elegir = (id) => {
    try { localStorage.setItem(almacen, id) } catch { /* modo privado */ }
    onChange(id)
    setAbierto(false)
  }

  if (cargando || negocios.length <= 1) return null

  const activo = negocios.find(n => n.id === valor)
  const etiqueta = valor === 'todos' ? 'Todos los negocios' : (activo?.nombre || 'Elegir negocio')

  const fila = (id, emoji, nombre, detalle, color) => {
    const sel = valor === id
    return (
      <button
        key={id}
        type="button"
        onClick={() => elegir(id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '9px 12px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: sel ? 'var(--bg-hover)' : 'transparent',
          borderLeft: `3px solid ${sel ? (color || 'var(--text)') : 'transparent'}`,
        }}
      >
        <span style={{ fontSize: 18, width: 22, textAlign: 'center' }}>{emoji}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: sel ? 700 : 500, color: 'var(--text)' }}>{nombre}</span>
          {detalle && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{detalle}</span>}
        </span>
        {sel && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>✓</span>}
      </button>
    )
  }

  return (
    <>
      <button
        ref={disparador}
        type="button"
        onClick={() => setAbierto(a => !a)}
        aria-expanded={abierto}
        aria-haspopup="menu"
        className="btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px',
          border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-card)',
          fontSize: 14, fontWeight: 600, color: 'var(--text)', cursor: 'pointer', maxWidth: '100%',
        }}
      >
        <span style={{ fontSize: 16 }}>{valor === 'todos' ? '🗂️' : (activo?.emoji || '🏢')}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{etiqueta}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>▾</span>
      </button>

      {abierto && createPortal(
        <div
          ref={menu}
          role="menu"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 1100,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            overflowY: 'auto', maxHeight: pos.maxHeight, padding: '6px 0',
          }}
        >
          {incluirTodos && (
            <>
              {fila('todos', '🗂️', 'Todos los negocios', `${negocios.length} negocios`, 'var(--text)')}
              <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
            </>
          )}
          {negocios.map(n => fila(n.id, n.emoji || '🏢', n.nombre, ETIQUETA_TIPO[n.tipo] || n.descripcion, n.color))}
        </div>,
        document.body,
      )}
    </>
  )
}
